<template>
  <div
    ref="flashcard-view-container"
    class="flashcard-view-container"
  >
    <flashcard
      :answered="answered"
      :answer-ids="answerIds"
      :correct="correct"
      :flashcard="currentFlashcard"
      :raw-answer-data="rawAnswerData"
      :show-rationale="true"
      :terminology="isTerminology"
      @set-answer-ids="setAnswerIds"
      @set-raw-answer-data="setRawAnswerData"
    />
    <flashcard-footer
      :answered="answered"
      :answer-ids="answerIds"
      :correct="correct"
      :flashcard="currentFlashcard"
      :raw-answer-data="rawAnswerData"
      :show-bookmark="true"
      :show-flashcard-statuses="true"
      @answer="onAnswered"
      @tag="onTag"
    />
  </div>
</template>

<script>
import Flashcard from '@components/flashcards/flashcard';
import FlashcardFooter from '@components/flashcards/footer';

import FlashcardViewMixin from '@/mixins/flashcardViews';
import GlobalMixin from '@/mixins/global';
import QuestionsUIV2Mixin from '@/mixins/questions-ui-v2';
import RateLimitMixin from '@/mixins/rate-limit';

export default {
  name: 'DeeplinkFlashcardView',
  components: {
    Flashcard, FlashcardFooter,
  },
  mixins: [FlashcardViewMixin, GlobalMixin, QuestionsUIV2Mixin, RateLimitMixin],
  computed: {
    currentFlashcardCategory() {
      return this.primaryFlashcardCategory(this.currentFlashcard);
    },
    nextFlashcardRoute() {
      return { name: 'home' };
    },
    shouldShowRateLimitModal() {
      if (this.isQotD) return false;
      if (!this.flashcardIsRateLimited(this.currentFlashcard, this.currentFlashcardCategory)) return false;
      if (!this.creditLimitReached) return false;
      return true;
    },
  },
  created() {
    this.maybeRedirectToCorrectVersion(this.currentFlashcard?.type);
    // TODO: This should not be subject to acl restrictions. The rateLimited param is the only way to bypass it on the api. We need to fix this in the future.
    this.$store.dispatch('flashcards/fetchFlashcards', { ids: [this.$route.params.flashcardId], rateLimited: true });
  },
  mounted() {
    const discussionId = this.$route.query.discussionId;
    // catch so a rejection can't go unhandled or throw past the rate-limit gating below
    const statePromise = this.$store.dispatch('flashcardStates/fetchStates', ['bookmarks', 'flashcards', 'stats']).catch(() => null);
    const discussionsPromise = discussionId
      ? this.$store.dispatch('discussions/fetchDiscussions', { flashcardId: this.$route.params.flashcardId }).catch(() => null)
      : null;

    this.$watch('currentFlashcard.id', async (newId, oldId) => {
      if (!newId || newId === oldId) return;

      if (discussionId && discussionsPromise) {
        await Promise.all([statePromise, discussionsPromise]);
        const discussions = this.$store.getters['discussions/getDiscussionsByFlashcardId'](newId) || [];
        const discussionIsReal = discussions.some(d => String(d.id) === String(discussionId));
        if (discussionIsReal) {
          this.restoreStateFromFlashcardState(this.currentFlashcard);
          // showAnswer opens the answer side and comments for an unanswered question, unless rate limited
          if (this.$route.query.showAnswer === 'true' && !this.answered && !this.shouldShowRateLimitModal) {
            this.answered = true;
          }
        }
      }

      if (!this.answered && this.shouldShowRateLimitModal) {
        // modal's beforeOpen bails on a falsy category; fall back so it never opens blank
        this.$modal.show('rate-limit-reached', {
          category: this.currentFlashcardCategory ?? { id: this.currentFlashcard?.categoryIds?.[0] },
          flashcard: this.currentFlashcard,
        });
      }
    }, { immediate: true });
  },
  methods: {
    onAnswered() {
      const correct = this.evaluateAnswer(this.currentFlashcard, this.answerIds, this.rawAnswerData);
      this.correct = correct;
      this.answered = true;
    },
    onTag(flashcardStatusId) {
      if (!this.hasBeenAnswered) {
        console.warn('deeplinked flashcard tag attempted before answer — routing home');
        this.goToNextFlashcard();
        return;
      }
      if (!this.currentFlashcardCategory) {
        console.warn('deeplinked flashcard has no in-app category — routing home');
        this.goToNextFlashcard();
        return;
      }
      this.tagFlashcard(flashcardStatusId);
    },
    restoreStateFromFlashcardState(flashcard) {
      if (!flashcard) {
        return;
      }

      const categoryId = this.currentFlashcardCategory?.id ?? flashcard?.categoryIds?.[0];
      const stateId = `${flashcard?.id}-category-${categoryId}`;
      const state = this.$store.getters['flashcardStates/flashcardStates'][stateId];
      if (!state) {
        return;
      }

      this.setAnswerIds(state.answerIds);
      this.setRawAnswerData(state.rawAnswerData);
      this.correct = this.evaluateAnswer(flashcard, this.answerIds, this.rawAnswerData);
      this.answered = true;
    },
  },
};
</script>

<style lang='scss' scoped>
  .flashcard-view-container {
    @include flashcard-view;
  }
</style>
